import api from './api';

export interface Driver {
  id: string;
  name: string;
  username: string;
  phone?: string;
  email?: string;
  eldSettings?: {
    ruleSet: string;
    cycle: string;
    restBreak: string;
    restRequirement: string;
  };
  licenseNumber?: string;
  licenseState?: string;
  locale?: string;
  timezone?: string;
  createdAtTime?: string;
  currentVehicleId?: string;
  currentVehicleName?: string;
  drivingStatus?: string;
}

export interface Vehicle {
  id: string;
  name: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  licensePlate?: string;
  notes?: string;
  gateway?: {
    model: string;
    serial: string;
  };
  engineHours?: number;
  odometerMeters?: number;
  fuelLevelPercent?: number;
  staticAssignedDriver?: {
    id: string;
    name: string;
  };
  updatedAtTime?: string;
}

export interface VehicleStats {
  vehicleId: string;
  startTime: string;
  endTime: string;
  engineState?: Array<{
    time: string;
    value: string;
  }>;
  fuelPercent?: Array<{
    time: string;
    value: number;
  }>;
  obdOdometerMeters?: Array<{
    time: string;
    value: number;
  }>;
}

export interface VehicleLocation {
  id: string;
  name?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    speed?: number;  // Speed in mph from Samsara
    heading?: number;  // Heading in degrees
    reverseGeo?: {
      formattedLocation?: string;
    };
    time?: string;
  };
  time?: string;
}

export interface DriverLocationFromHos {
  driverId: string;
  driverName: string;
  vehicleId: string;
  location: {
    latitude: number;
    longitude: number;
  };
  hosStatus: string;  // 'driving', 'onDuty', 'offDuty', etc.
  logTime: string;
  totalLogs: number;
}

export interface FuelEnergyData {
  data: VehicleStats[];
  pagination?: {
    hasNextPage: boolean;
    hasPrevPage: boolean;
    startCursor?: string;
    endCursor?: string;
  };
}

export interface SafetyScore {
  driverId: string;
  driverName: string;
  safetyScore: number;
  crashCount: number;
  harshAccelCount: number;
  harshBrakingCount: number;
  harshTurningCount: number;
  distractedDrivingCount: number;
  totalDistanceDrivenMeters: number;
  totalTimeDrivenMs: number;
}

export interface Trip {
  id: string;
  driverId?: string;
  vehicleId: string;
  startTime: string;
  endTime: string;
  startLocation: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  endLocation: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  distanceMeters: number;
  tollMeters?: number;
  fuelConsumedMl?: number;
}

export interface DriverVehicleAssignment {
  driverId: string;
  driverName?: string;
  vehicleId: string;
  vehicleName?: string;
  assignedAtTime: string;
  staticAssignmentId?: string;
}

export interface StaticVehicleAssignment {
  vehicleId: string;
  vehicleName: string;
  licensePlate?: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  driverId: string;
  driverName: string;
  gatewaySerial?: string;
  gatewayModel?: string;
  esn?: string;
  cameraSerial?: string;
  v1DriverFirstName?: string;
  v1DriverLastName?: string;
  v1DriverActive?: string;
  v1UnitStatus?: string;
  v1TruckVin?: string;
  fetchedAt?: string;
}

export interface EquipmentLocation {
  id: string;
  name?: string;
  latitude: number;
  longitude: number;
  latitudeDirection?: string;
  longitudeDirection?: string;
  locatedAtTime: string;
  headingDegrees?: number;
  speedMilesPerHour?: number;
  reverseGeo?: {
    formattedLocation?: string;
  };
}

export interface HosClock {
  driver?: {
    id: string;
    name?: string;
  };
  currentVehicle?: {
    id: string;
    name?: string;
  };
  currentDutyStatus?: {
    hosStatusType?: string;
  };
  clocks?: {
    timeUntilBreak?: string;
    timeInCurrentStatus?: string;
    driveTimeRemaining?: string;
    shiftTimeRemaining?: string;
    cycleTimeRemaining?: string;
    breakTimeRemaining?: string;
    dutyTimeRemaining?: string;
  };
  violations?: {
    violationAlerts?: string[];
  };
  // Legacy flat structure fields (for backward compatibility)
  driverId?: string;
  driverName?: string;
  timeUntilBreak?: string;
  timeInCurrentStatus?: string;
  driveTimeRemaining?: string;
  shiftTimeRemaining?: string;
  cycleTimeRemaining?: string;
  breakTimeRemaining?: string;
  dutyTimeRemaining?: string;
  violationAlerts?: string[];
}

export interface Gateway {
  serial: string;
  model: string;
  asset?: {
    id: string;
    externalIds?: {
      [key: string]: string;
    };
  };
  connectionStatus?: {
    healthStatus: string;
    lastConnected?: string;
  };
  dataUsageLast30Days?: {
    cellularDataUsageBytes?: number;
    hotspotUsageBytes?: number;
  };
}

export interface HosSummary {
  violations: {
    total: number;
    driversAffected: number;
  };
  driversOnDuty: number;
  lowDriveTime: number;
  activeAssignments: number;
  timestamp: string;
}

export interface V1DriverData {
  truckvin?: string;
  unitnumber?: string;
  unit_status?: string;
  driver_firstname?: string;
  driver_lastname?: string;
  driver_active?: string;
}

class SamsaraService {
  private baseUrl = '/samsara';

  async getDashboardData(): Promise<any> {
    // OPTIMIZATION: Use database endpoint instead of hitting Samsara API
    // This endpoint queries FastCMS database tables (samsara_raw_*) which are pre-populated
    const response = await api.get('/dispatching/dashboard');
    return response.data;
  }

  async getDrivers(includeRemoteData = false, limit?: number, enrichWithHos = true, signal?: AbortSignal): Promise<{ data: Driver[] }> {
    const params: any = { include_remote_data: includeRemoteData, enrich_with_hos: enrichWithHos };
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/drivers`, { params, signal });
    return response.data;
  }

  async getDriver(driverId: string): Promise<Driver> {
    const response = await api.get(`${this.baseUrl}/drivers/${driverId}`);
    return response.data;
  }

  async getVehicles(tagIds?: string[], vehicleIds?: string[], onlyAssigned?: boolean, limit?: number, signal?: AbortSignal): Promise<{ data: Vehicle[] }> {
    const params: any = {};
    if (tagIds) params.tag_ids = tagIds.join(',');
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');
    if (onlyAssigned) params.only_assigned = true;
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/vehicles`, { params, signal });
    return response.data;
  }

  async getVehicle(vehicleId: string): Promise<Vehicle> {
    const response = await api.get(`${this.baseUrl}/vehicles/${vehicleId}`);
    return response.data;
  }

  async getVehicleByVin(vin: string): Promise<Vehicle> {
    const response = await api.get(`${this.baseUrl}/vehicles/by-vin/${vin}`);
    return response.data;
  }

  async getVehicleStats(
    vehicleIds?: string[],
    statTypes?: string[],
    hoursBack = 24
  ): Promise<FuelEnergyData> {
    const params: any = { hours_back: hoursBack };
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');
    if (statTypes) params.stat_types = statTypes.join(',');

    const response = await api.get(`${this.baseUrl}/vehicles/stats`, { params });
    return response.data;
  }

  async getFuelAndEnergy(
    vehicleIds?: string[],
    hoursBack = 24
  ): Promise<FuelEnergyData> {
    const params: any = { hours_back: hoursBack };
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');

    const response = await api.get(`${this.baseUrl}/fuel-energy`, { params });
    return response.data;
  }

  async getVehicleLocations(vehicleIds?: string[], limit?: number): Promise<{ data: VehicleLocation[] }> {
    const params: any = {};
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');
    if (limit) params.limit = limit;

    // Use test-pagination endpoint which has pagination working (bypasses old cached endpoint)
    const response = await api.get(`${this.baseUrl}/vehicles/locations/test-pagination`, { params });
    return response.data;
  }

  async getDriverLocationsFromHos(hoursBack: number = 24): Promise<{ data: DriverLocationFromHos[], count: number }> {
    const response = await api.get(`${this.baseUrl}/drivers/locations-from-hos`, {
      params: { hours_back: hoursBack }
    });
    return response.data;
  }

  async getDriverSafetyScores(driverIds?: string[], limit?: number): Promise<{ data: SafetyScore[] }> {
    const params: any = {};
    if (driverIds) params.driver_ids = driverIds.join(',');
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/drivers/safety-scores`, { params });
    return response.data;
  }

  async getHosClocks(driverIds?: string[], limit?: number, signal?: AbortSignal): Promise<{ data: HosClock[] }> {
    const params: any = {};
    if (driverIds) params.driver_ids = driverIds.join(',');
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/hos-clocks`, { params, signal });
    return response.data;
  }

  async getHosLogs(
    driverIds?: string[],
    hoursBack = 24
  ): Promise<any> {
    const params: any = { hours_back: hoursBack };
    if (driverIds) params.driver_ids = driverIds.join(',');

    const response = await api.get(`${this.baseUrl}/hos-logs`, { params });
    return response.data;
  }

  async getTrips(
    vehicleIds?: string[],
    driverIds?: string[],
    hoursBack = 24,
    limit?: number
  ): Promise<{ data: Trip[] }> {
    const params: any = { hours_back: hoursBack };
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');
    if (driverIds) params.driver_ids = driverIds.join(',');
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/trips`, { params });
    return response.data;
  }

  async getDriverVehicleAssignments(limit?: number): Promise<{ data: DriverVehicleAssignment[] }> {
    const params: any = {};
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/fleet/drivers/assignments`, { params });
    return response.data;
  }

  async getStaticVehicleAssignments(
    driverIds?: string[],
    vehicleIds?: string[],
    limit?: number,
    includeV1Data?: boolean
  ): Promise<{ data: StaticVehicleAssignment[] }> {
    const params: any = {};
    if (driverIds) params.driver_ids = driverIds.join(',');
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');
    if (limit) params.limit = limit;
    if (includeV1Data) params.include_v1_data = includeV1Data;

    const response = await api.get(`${this.baseUrl}/fleet/static-assignments`, { params });
    return response.data;
  }

  async getEquipmentLocations(vehicleIds?: string[], limit?: number): Promise<{ data: EquipmentLocation[] }> {
    const params: any = {};
    if (vehicleIds) params.vehicle_ids = vehicleIds.join(',');
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/fleet/equipment/locations`, { params });
    return response.data;
  }

  async getGateways(limit?: number, signal?: AbortSignal): Promise<{ data: Gateway[] }> {
    const params: any = {};
    if (limit) params.limit = limit;

    const response = await api.get(`${this.baseUrl}/gateways`, { params, signal });
    return response.data;
  }

  async getGateway(serial: string): Promise<Gateway> {
    const response = await api.get(`${this.baseUrl}/gateways/${serial}`);
    return response.data;
  }

  async getHosSummary(signal?: AbortSignal): Promise<HosSummary> {
    const response = await api.get(`${this.baseUrl}/hos-summary`, { signal });
    return response.data;
  }

  async callSamsaraAPI(endpoint: string, params?: Record<string, any>): Promise<any> {
    // Generic proxy to call any Samsara API endpoint
    // endpoint should not have leading slash (e.g., 'addresses' not '/addresses')
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await api.get(`${this.baseUrl}/proxy/${cleanEndpoint}`, { params });
    return response.data;
  }

  async deleteVehicles(vehicleIds: string[]): Promise<{ deleted_count: number; failed_count: number; deleted: string[]; failed: any[]; message: string }> {
    const params = { vehicle_ids: vehicleIds.join(',') };
    const response = await api.delete(`${this.baseUrl}/fleet/vehicles`, { params });
    return response.data;
  }

  async getV1DriverDataByVin(vin: string): Promise<V1DriverData> {
    const response = await api.get(`/safety/trucks/v1-driver-data/by-vin/${vin}`);
    return response.data;
  }
}

export default new SamsaraService();